import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import { useField } from 'formik';
import { defaultCountries, CountryData, CountrySelector, CountryIso2, ParsedCountry } from 'react-international-phone';
import enLabels from 'react-phone-number-input/locale/en.json';
import esLabels from 'react-phone-number-input/locale/es.json';
import deLabels from 'react-phone-number-input/locale/de.json';
import frLabels from 'react-phone-number-input/locale/fr.json';
import itLabels from 'react-phone-number-input/locale/it.json';

import { getDialCodeByIso2, normalizePhoneWithPlus, suggestCountryFromPhone } from './helpers';
import { Language } from 'models/settings';
import Error from '../../Error';

import css from './Phone.module.scss';

type Props = {
  name: string;
  placeholder?: string;
  locale?: Language;
  type?: 'huge' | 'primary';
  disableDialCodePrefill?: boolean;
};

const COUNTRIES_TRANSLATIONS: Record<string, Record<string, string>> = {
  en: enLabels,
  es: esLabels,
  de: deLabels,
  fr: frLabels,
  it: itLabels,
};

export const Phone = ({
  name,
  placeholder,
  locale = 'en',
  type = 'primary',
  disableDialCodePrefill = false,
}: Props) => {
  const [countryCode, setCountry] = useState<CountryIso2>(locale === 'en' ? 'us' : locale);
  const inputRef = useRef<HTMLInputElement>(null);
  const phoneFieldRef = useRef<HTMLInputElement>(null);
  const [field, meta, helpers] = useField(name);
  const errorMsg = !!(meta.touched && meta.error) ? meta.error : '';

  useEffect(() => {
    // TODO: (v)think more about it
    if (field.value) {
      const dealCode = suggestCountryFromPhone(field.value);
      setCountry(dealCode || 'us');
    }
  }, []);

  useEffect(() => {
    if (!disableDialCodePrefill) {
      const dialCode = getDialCodeByIso2(locale === 'en' ? 'us' : locale);
      if (dialCode) helpers.setValue(`+${dialCode}`);
    }
  }, [locale]);

  const translatedCountries: CountryData[] = defaultCountries.map((c) => {
    const [label, iso2, dialCode, ...rest] = c;
    return [COUNTRIES_TRANSLATIONS[locale][iso2.toUpperCase()] || label, iso2, dialCode, ...rest] as CountryData;
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = normalizePhoneWithPlus(e.target.value);

    const suggested = suggestCountryFromPhone(e.target.value);
    if (suggested) {
      setCountry(suggested);
    }

    helpers.setValue(val);
  };

  return (
    <div
      ref={phoneFieldRef}
      className={cn(css.phoneField, type === 'huge' && css.huge, type == 'primary' && css.primary)}
    >
      <div className={css.phoneFieldInner}>
        <input
          type="tel"
          ref={inputRef}
          placeholder={placeholder}
          value={field.value}
          maxLength={20}
          onChange={handleChange}
          onBlur={() => helpers.setTouched(true)}
          className={css.phoneInput}
        />
        <CountrySelector
          selectedCountry={countryCode}
          countries={translatedCountries}
          onSelect={({ iso2, dialCode }: ParsedCountry) => {
            setCountry(iso2);
            helpers.setValue(`+${dialCode}`);
            inputRef.current?.focus();
          }}
          className={css.countrySelector}
          buttonClassName={css.dropdownButton}
          dropdownArrowClassName={css.dropdownArrow}
          flagClassName={css.flag}
          dropdownStyleProps={{
            className: css.dropdown,
            listItemClassName: css.listItem,
            listItemCountryNameClassName: css.countryName,
            listItemDialCodeClassName: css.dialCode,
            style: {
              width: `${phoneFieldRef.current?.offsetWidth}px`,
            },
          }}
        />
      </div>

      {errorMsg && meta.touched && <Error name={name} error={errorMsg} />}
    </div>
  );
};
